/**
 * Shared row-primitives for the tree editor.
 *
 * - `DragHandle`: GripVertical icon with sortable handle ref + a11y attrs.
 * - `DrillDownButton`: chevron button that opens the detail panel.
 * - `DndRowMenu`: ContextMenu wrapper with Delete (destructive) + Duplicate
 *   items. `children` is the trigger row content. Duplicate is shown only
 *   when `onDuplicate` is supplied.
 */

import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { ChevronRight, Copy, GripVertical, Trash2 } from 'lucide-react';
import type { ReactNode, Ref } from 'react';

interface DragHandleProps {
  handleRef: Ref<HTMLDivElement>;
  ariaLabel: string;
}

export function DragHandle({ handleRef, ariaLabel }: DragHandleProps) {
  return (
    <div
      ref={handleRef}
      role="button"
      aria-roledescription="sortable"
      aria-label={ariaLabel}
      tabIndex={0}
      className="flex w-8 shrink-0 cursor-grab items-center justify-center active:cursor-grabbing"
    >
      <GripVertical className="text-muted-foreground/60 h-4 w-4" />
    </div>
  );
}

interface DrillDownButtonProps {
  onClick: () => void;
  ariaLabel: string;
}

export function DrillDownButton({ onClick, ariaLabel }: DrillDownButtonProps) {
  return (
    <button
      type="button"
      className="text-muted-foreground hover:text-foreground mr-1 flex h-8 w-8 shrink-0
        items-center justify-center rounded-md transition-colors hover:bg-black/5"
      onClick={onClick}
      aria-label={ariaLabel}
    >
      <ChevronRight className="h-4 w-4" />
    </button>
  );
}

interface DndRowMenuProps {
  onDelete: () => void;
  deleteLabel: string;
  onDuplicate?: () => void;
  children: ReactNode;
}

export function DndRowMenu({
  onDelete,
  deleteLabel,
  onDuplicate,
  children,
}: DndRowMenuProps) {
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem onClick={onDelete} variant="destructive">
          <Trash2 className="mr-2 h-4 w-4" />
          {deleteLabel}
        </ContextMenuItem>
        {onDuplicate && (
          <ContextMenuItem onClick={onDuplicate}>
            <Copy className="mr-2 h-4 w-4" />
            Duplicate
          </ContextMenuItem>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
}
